// @/services/auth/sessionTimeout.js
import React from 'react';
import { createRoot } from 'react-dom/client';
import SessionTimeoutWarning from '@/components/auth/SessionTimeoutWarning';

/**
 * SessionTimeoutManager
 * 
 * Manages session timeout monitoring and logout for inactive users:
 * - Tracks user activity across multiple tabs
 * - Shows warning dialog before timeout occurs
 * - Implements graceful session termination
 * - Provides cross-tab activity synchronization
 * - Handles tab visibility changes and browser focus events
 * - Performs token validation and refresh on tab focus
 * 
 * MIGRATED: Now uses LogoutService for proper logout handling
 */

export class SessionTimeoutManager {
  /**
   * Initialize the session timeout manager
   * @param {Object} authService - Authentication service instance
   */
  constructor(authService) {
    // Store auth service reference
    this.authService = authService;

    // Configuration
    this.config = {
      inactivityTimeout: process.env.NODE_ENV === 'development'
        ? 29 * 60 * 1000  // 29 minutes for dev
        : 30 * 60 * 1000,  // 30 minutes to match Keycloak

      // 2 minutes warning for both dev and production
      warningTime: 2 * 60 * 1000,  // 2 minutes warning

      // Debug on for dev
      debug: process.env.NODE_ENV === 'development'
    };

    // State management
    this.state = {
      initialized: false,
      lastActivity: Date.now(),
      timeoutTimer: null,
      warningTimer: null,
      warningDisplayed: false,
      isActive: true
    };

    // Store bound method references to avoid recreation on each event
    this.activityHandler = this.handleUserActivity.bind(this);
    this.refreshSession = this.resetTimers.bind(this);
    this.visibilityHandler = this.handleVisibilityChange.bind(this);
    this.continueSession = this.handleUserActivity.bind(this);
    this.logoutSession = this.performTimeout.bind(this);

    // Cross-tab synchronization key
    this.ACTIVITY_SYNC_KEY = 'lastUserActivity';

    // Reference to warning component container and root
    this.warningContainer = null;
    this.warningRoot = null;
  }

  /**
   * Initialize timeout monitoring
   * Sets up event listeners, creates warning container, and starts timers
   * 
   * @returns {boolean} Success status
   */
  initialize() {
    if (this.state.initialized) return;

    if (typeof window === 'undefined') return;

    this.log('Initializing session timeout monitoring');

    // Register activity listeners
    window.addEventListener('mousemove', this.activityHandler);
    window.addEventListener('mousedown', this.activityHandler);
    window.addEventListener('click', this.activityHandler);
    window.addEventListener('keypress', this.activityHandler);
    window.addEventListener('scroll', this.activityHandler);
    window.addEventListener('touchstart', this.activityHandler);

    // Handle tab visibility changes
    document.addEventListener('visibilitychange', this.visibilityHandler);

    // Set up storage listener for cross-tab synchronization
    window.addEventListener('storage', (event) => {
      if (event.key === this.ACTIVITY_SYNC_KEY) {
        try {
          const storedActivity = JSON.parse(event.newValue);
          if (storedActivity && storedActivity.timestamp) {
            // Only update if the stored timestamp is newer
            if (storedActivity.timestamp > this.state.lastActivity) {
              this.log('Detected activity in another tab');
              this.state.lastActivity = storedActivity.timestamp;
              this.resetTimers();
            }
          }
        } catch (e) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('Error parsing activity data from another tab:', e);
          }
        }
      }
    });

    // Create warning container div
    this.createWarningContainer();

    // Initial timer setup
    this.resetTimers();

    this.state.initialized = true;

    return true;
  }

  /**
   * Create container for the warning dialog
   * @private
   */
  createWarningContainer() {
    if (typeof document === 'undefined') return;

    if (!this.warningContainer) {
      // Create a div for the modal
      const container = document.createElement('div');
      container.id = 'session-timeout-warning-container';
      document.body.appendChild(container);
      this.warningContainer = container;

      // Create React root for rendering
      this.warningRoot = createRoot(container);
    }
  }

  /**
   * Stop timeout monitoring and clean up
   * Removes event listeners, clears timers, and unmounts React components
   * 
   * @returns {boolean} Success status
   */
  shutdown() {
    if (!this.state.initialized) return;

    this.log('Shutting down session timeout monitoring');

    // Clear all timers
    if (this.state.timeoutTimer) {
      clearTimeout(this.state.timeoutTimer);
      this.state.timeoutTimer = null;
    }

    if (this.state.warningTimer) {
      clearTimeout(this.state.warningTimer);
      this.state.warningTimer = null;
    }

    // Remove event listeners
    if (typeof window !== 'undefined') {
      window.removeEventListener('mousemove', this.activityHandler);
      window.removeEventListener('mousedown', this.activityHandler);
      window.removeEventListener('click', this.activityHandler);
      window.removeEventListener('keypress', this.activityHandler);
      window.removeEventListener('scroll', this.activityHandler);
      window.removeEventListener('touchstart', this.activityHandler);
      document.removeEventListener('visibilitychange', this.visibilityHandler);
    }

    // Hide warning if displayed
    if (this.state.warningDisplayed) {
      this.hideWarning();
    }

    // Unmount React root and remove container
    if (this.warningRoot) {
      try {
        this.warningRoot.unmount();
        this.warningRoot = null;
      } catch (e) {
        console.error('Error unmounting React component:', e);
      }
    }

    if (this.warningContainer && document.body.contains(this.warningContainer)) {
      try {
        document.body.removeChild(this.warningContainer);
        this.warningContainer = null;
      } catch (e) {
        console.error('Error removing warning container:', e);
      }
    }

    this.state.initialized = false;
    return true;
  }

  /**
   * Handle user activity events
   * Updates activity timestamp and resets timers
   * @private
   */
  handleUserActivity() {
    // Skip if not initialized
    if (!this.state.initialized) return;

    // Throttle activity updates for performance
    const now = Date.now();
    const timeSinceLastActivity = now - this.state.lastActivity;

    // Only process if it's been at least 1 second since last activity
    // or if the warning is currently displayed
    if (timeSinceLastActivity < 1000 && !this.state.warningDisplayed) {
      return;
    }

    // Update last activity time
    this.state.lastActivity = now;
    this.state.isActive = true;

    // Synchronize across tabs
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(this.ACTIVITY_SYNC_KEY, JSON.stringify({
          timestamp: now
        }));
      }
    } catch (e) {
      // Ignore storage errors
    }

    // Reset timers
    this.resetTimers();

    // Hide warning if displayed
    if (this.state.warningDisplayed) {
      this.hideWarning();
    }
  }

  /**
   * Handle tab visibility changes
   * Checks session validity when tab becomes visible
   * @private
   */
  handleVisibilityChange() {
    if (document.visibilityState === 'visible') {
      // Check if session is still valid on tab focus
      this.checkSessionValidity();

      // The tab became visible, treat it as user activity
      this.handleUserActivity();
    }
  }

  /**
   * Check if session is still valid
   * Verifies token validity and session timeout status
   * 
   * @private
   * @returns {Promise<boolean>} Session validity status
   */
  async checkSessionValidity() {
    // Skip if not initialized
    if (!this.state.initialized) return true;

    // If no auth service or not authenticated, no need to check
    if (!this.authService || !this.authService.getAuthState()?.authenticated) {
      return false;
    }

    // Check time since last activity
    const now = Date.now();
    const elapsed = now - this.state.lastActivity;

    // If we've been inactive longer than the timeout, force logout
    if (elapsed >= this.config.inactivityTimeout) {
      this.log(`Inactive for ${Math.round(elapsed / 1000)}s, triggering logout`);
      this.performTimeout();
      return false;
    }

    // If we have a refresh token method, try to refresh token
    if (this.authService.refreshToken) {
      try {
        const refreshed = await this.authService.refreshToken();
        if (!refreshed) {
          // If refresh failed, session might be invalid
          this.log('Token refresh failed, session may be invalid');
          return false;
        }
      } catch (e) {
        this.log('Error during token refresh check:', e);
        return false;
      }
    }

    return true;
  }

  /**
   * Reset the timeout timers
   * Sets up warning and timeout timers based on configuration
   * @private
   */
  resetTimers() {
    // Clear existing timers
    if (this.state.timeoutTimer) {
      clearTimeout(this.state.timeoutTimer);
    }

    if (this.state.warningTimer) {
      clearTimeout(this.state.warningTimer);
    }

    // Hide warning if displayed
    if (this.state.warningDisplayed) {
      this.hideWarning();
    }

    // Set new warning timer
    const warningDelay = this.config.inactivityTimeout - this.config.warningTime;
    this.state.warningTimer = setTimeout(() => {
      this.showWarning();
    }, warningDelay);

    // Set new timeout timer
    this.state.timeoutTimer = setTimeout(() => {
      this.performTimeout();
    }, this.config.inactivityTimeout);

    // Timer reset logging silenced - too verbose
  }

  /**
   * Show the session expiration warning
   * Renders the warning dialog with countdown
   * @private
   */
  showWarning() {
    // Skip if not initialized
    if (!this.state.initialized) return;

    this.log('Showing session expiration warning');
    this.state.warningDisplayed = true;

    // Ensure warning container exists
    if (!this.warningContainer || !this.warningRoot) {
      this.createWarningContainer();
    }

    try {
      // Calculate seconds remaining before logout
      const secondsRemaining = Math.ceil(this.config.warningTime / 1000);

      // Render the React component into the container
      this.warningRoot.render(
        <SessionTimeoutWarning
          open={true}
          onContinue={this.continueSession}
          onLogout={this.logoutSession}
          secondsRemaining={secondsRemaining}
        />
      );
    } catch (e) {
      console.error('Error showing session timeout warning:', e);
    }

    // Also try to refresh the token as a last chance measure
    if (this.authService && this.authService.refreshToken) {
      this.authService.refreshToken().catch(() => {
        // Ignore errors here, we'll log out shortly if needed
      });
    }
  }

  /**
   * Hide the session expiration warning
   * @private
   */
  hideWarning() {
    if (!this.state.warningDisplayed || !this.warningRoot) return;

    this.log('Hiding session expiration warning');
    this.state.warningDisplayed = false;

    try {
      // Render with open=false to hide the dialog
      this.warningRoot.render(
        <SessionTimeoutWarning
          open={false}
          onContinue={this.continueSession}
          onLogout={this.logoutSession}
          secondsRemaining={0}
        />
      );
    } catch (e) {
      console.error('Error hiding session timeout warning:', e);
    }
  }

  /**
   * Perform session timeout and logout
   * Ensures clean logout URL without auth params
   * @private
   */
  async performTimeout() {
    this.log('Performing session timeout and logout');

    // Set all logout markers
    sessionStorage.setItem('controlledLogout', 'true');
    sessionStorage.setItem('logout_initiator', 'true');
    sessionStorage.setItem('logoutReason', 'inactivity');
    sessionStorage.setItem('sessionTimeout', 'true');
    sessionStorage.setItem('skipAuthProcessing', 'true');

    // Clear localStorage items
    localStorage.removeItem('firstRunComplete');
    localStorage.removeItem('showFirstRunWelcome');
    localStorage.removeItem('crossTabAuthInfo');

    // Clear rebrand-cleanup gate localStorage so the next user on this
    // device hits the terms gate + announcements fresh. Also sweeps any
    // legacy relaunchSeen_* / relaunchCheckTimestamp stragglers.
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (
        key &&
        (key.startsWith('termsAccepted_') ||
          key.startsWith('announcement_state_') ||
          key === 'announcement_check_at' ||
          key.startsWith('relaunchSeen_') ||
          key === 'relaunchCheckTimestamp')
      ) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));

    // Session-scoped Terms gate Later flags
    try {
      const sessionKeys = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const k = sessionStorage.key(i);
        if (k && k.startsWith('termsLaterDeferred_')) sessionKeys.push(k);
      }
      sessionKeys.forEach(k => sessionStorage.removeItem(k));
    } catch (e) {
      // Ignore
    }

    // Use logoutService which will find idToken from cache
    const { logoutService } = await import('@/services/auth/logout');
    await logoutService.logout({
      reason: 'inactivity',
      isInitiator: true
    });
  }

  /**
   * Log debug messages when debug mode is enabled
   * @private
   * @param {...*} args - Arguments to log
   */
  log(...args) {
    if (this.config.debug) {
      console.log('[SessionTimeout]', ...args);
    }
  }
}

// Singleton instance
let instance = null;

/**
 * Initialize session timeout with auth service
 * Creates or returns the singleton instance
 * 
 * @param {Object} authService - Authentication service
 * @returns {SessionTimeoutManager} Session timeout manager instance
 */
export const initSessionTimeout = (authService) => {
  if (!instance) {
    instance = new SessionTimeoutManager(authService);
  }
  return instance;
};

/**
 * Get session timeout manager instance
 * 
 * @returns {SessionTimeoutManager|null} Session timeout manager instance or null if not initialized
 */
export const getSessionTimeout = () => instance;

export default initSessionTimeout;